import { Component, Input, computed, signal } from '@angular/core';
import { TextWidget, Theme, WidgetColors } from '../../models/reader-document';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked, type Tokens } from 'marked';
import { DEFAULT_COLORS } from '../../utils/widget.constants';
import { resolveWidgetStyleColors } from '../../utils/widget-style.utils';

@Component({
  selector: 'wr-text-widget',
  standalone: true,
    template: `
    <div
      class="wr-text-widget px-4"
      [style.background]="background()"
      [style.color]="color()"
      [style.font-family]="fontFamily()"
      [style.padding-top]="paddingTop()"
      [style.padding-bottom]="paddingBottom()"
    >
      <div
        class="w-full px-1"
        [style.text-align]="textAlign()"
      >
        <!-- Title / Subtitle -->
        @if (widget()?.title || widget()?.subtitle) {
          <div class="mb-2">
            @if (widget()?.title) {
              <div
                [style.color]="titleColor()"
                [style.font-family]="titleFont()"
                class="title"
              >
                {{ widget()?.title }}
              </div>
            }

            @if (widget()?.subtitle) {
              <div
                [style.color]="subtitleColor()"
                [style.font-family]="subtitleFont()"
                class="subtitle"
              >
                {{ widget()?.subtitle }}
              </div>
            }
          </div>
        }

        <!-- Image + Markdown -->
        <div>
          @if (hasImage() && imageSrc()) {
            @if (imageLink()) {
              <a
                [href]="imageLink()"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  [src]="imageSrc()!"
                  [alt]="imageAlt()"
                  [style.width.%]="35"
                  [style.border-radius.px]="imageCornerRadius()"
                  style="object-fit: cover; margin-bottom: 10px;"
                  [style.float]="imageFloat()"
                  [style.margin-right.px]="imageFloat() === 'left' ? 24 : 0"
                  [style.margin-left.px]="imageFloat() === 'right' ? 24 : 0"
                  (error)="onImgError($event)"
                />
              </a>
            } @else {
              <img
                [src]="imageSrc()!"
                [alt]="imageAlt()"
                [style.width.%]="40"
                [style.border-radius.px]="imageCornerRadius()"
                style="object-fit: cover; margin-bottom: 10px;"
                [style.float]="imageFloat()"
                [style.margin-right.px]="imageFloat() === 'left' ? 24 : 0"
                [style.margin-left.px]="imageFloat() === 'right' ? 24 : 0"
                (error)="onImgError($event)"
              />
            }
          }

          <div
            class="markdown"
            [style.--font-plain]="plainFont()"
            [style.--font-bold]="boldFont()"
            [style.--font-italic]="italicFont()"
            [style.--accent]="accentColor()"
            [innerHTML]="markdownHtml()"
          ></div>

          <!-- Clear float -->
          <div style="clear: both;"></div>
        </div>
      </div>
    </div>
  `,
})
export class TextWidgetComponent {
  private widgetSig = signal<TextWidget | null>(null);
  private themeSig = signal<Theme & { isDark?: boolean } | null>(null);
  private imgSrcSig = signal<string | null>(null);

  constructor(private sanitizer: DomSanitizer) {
    const renderer = {
      link({ href, title, text }: Tokens.Link) {
        const safeHref = href ?? '';
        const safeTitle = title ? title.replace(/"/g, '&quot;') : '';
        const isAnchor = safeHref.startsWith('#');
        const isChild = safeHref.startsWith('child:');
        const titleAttr = safeTitle ? ` title="${safeTitle}"` : '';

        // #asd or child:asd → open in same tab
        if (isAnchor || isChild) {
          return `<a href="${safeHref}"${titleAttr}>${text}</a>`;
        }
        // everything else → new tab
        return `<a href="${safeHref}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
      }
    };

    marked.use({ renderer, breaks: true });
  }

  @Input() set json(value: TextWidget) {
    this.widgetSig.set(value);

     const img = value?.image;
    if (img) {
      const primary = img.url || null;
      const fallback = img.fallbackUrl || null;
      this.imgSrcSig.set(primary || fallback);
    } else {
      this.imgSrcSig.set(null);
    }
    
  }

  @Input() set theme(value: Theme | undefined) {
    this.themeSig.set(value ?? null);
  }

  widget = computed(() => this.widgetSig());

  textAlign = computed(() => {
    const raw = this.widget()?.textAlignment || 'left';

    if (raw === 'left' || raw === 'right' || raw === 'center' || raw === 'justify') {
      return raw;
    }

    return 'right';
  });

  mergedColors = computed<WidgetColors>(() => {
    const widget = this.widget();
    const theme = this.themeSig();
    const isDark = theme?.isDark ?? false;

    const themeColors: WidgetColors = theme
      ? {
          accent: theme[isDark ? 'darkColors' : 'lightColors'].accent,
          title: theme[isDark ? 'darkColors' : 'lightColors'].title,
          subtitle: theme[isDark ? 'darkColors' : 'lightColors'].subtitle,
          primary: theme[isDark ? 'darkColors' : 'lightColors'].primary,
          background: theme[isDark ? 'darkColors' : 'lightColors'].background,
        }
      : {};

    const widgetColors: WidgetColors = resolveWidgetStyleColors(widget?.widgetStyle, isDark);

    return {
        accent: widgetColors.accent ?? themeColors.accent ?? DEFAULT_COLORS.accent,
        title: widgetColors.title ?? themeColors.title ?? DEFAULT_COLORS.title,
        subtitle: widgetColors.subtitle ?? themeColors.subtitle ?? DEFAULT_COLORS.subtitle,
        primary: widgetColors.primary ?? themeColors.primary ?? DEFAULT_COLORS.primary,
        background: widgetColors.background ?? themeColors.background ?? DEFAULT_COLORS.background,
      };
  });

  paddingTop = computed(() => {
    const pad = this.widget()?.widgetStyle?.padding?.top ?? 0;
    return `${pad}px`;
  });

  paddingBottom = computed(() => {
    const pad = this.widget()?.widgetStyle?.padding?.bottom ?? 0;
    return `${pad}px`;
  });

  hasImage = computed(() => {
    const w = this.widget();
    const img = w?.image;
    return !!img && (!!img.url || !!img.fallbackUrl);
  });

  imageSrc = computed(() => this.imgSrcSig());

  imageLink = computed(() => this.widget()?.image?.linkURL || null);

  imageFloat = computed<'left' | 'right'>(() =>
    this.widget()?.imagePosition === 'right' ? 'right' : 'left'
  );

  imageAlt = computed(
    () =>
      this.widget()?.image?.altTitle ||
      this.widget()?.title ||
      this.widget()?.name ||
      this.widget()?.id ||
      ''
  );

  imageCornerRadius = computed(() => {
    const base = this.themeSig()?.cornerRadius ?? 0;
    return Math.round(base * 0.8);
  });

  // fonts for markdown
  boldFont = computed(() =>
    this.themeSig()?.fonts?.bold?.name ??
    this.themeSig()?.fonts?.plain?.name ??
    'inherit'
  );

  italicFont = computed(() =>
    this.themeSig()?.fonts?.italic?.name ??
    this.themeSig()?.fonts?.plain?.name ??
    'inherit'
  );

  plainFont = computed(() =>
    this.themeSig()?.fonts?.plain?.name ??
    'inherit'
  );

  fontFamily = this.plainFont;

  // fonts for title/subtitle
  titleFont = computed(() =>
    this.themeSig()?.fonts?.title?.name ??
    this.themeSig()?.fonts?.plain?.name ??
    'inherit'
  );

  subtitleFont = computed(() =>
    this.themeSig()?.fonts?.subtitle?.name ??
    this.themeSig()?.fonts?.plain?.name ??
    'inherit'
  );

  // colors
  accentColor = computed(() => this.mergedColors()?.accent);
  titleColor = computed(() => this.mergedColors()?.title);
  subtitleColor = computed(() => this.mergedColors()?.subtitle);
  background = computed(() => this.mergedColors()?.background);
  color = computed(() => this.mergedColors()?.primary);

  markdownHtml = computed<SafeHtml | null>(() => {
    const w = this.widget();
    if (!w) return null;

    let text = w.text ?? '';

    // Remove empty list items
    text = text.replace(/^[ \t]*([*-]|\d+\.)[ \t]*\n/gm, '');

    const rawHtml = marked.parse(text, { async: false }) as string;
    return this.sanitizer.bypassSecurityTrustHtml(rawHtml);
  });

  onImgError(event: Event) {
    const w = this.widget();
    const img = w?.image;
    if (!img) return;

    if (img.fallbackUrl && this.imgSrcSig() !== img.fallbackUrl) {
      this.imgSrcSig.set(img.fallbackUrl);
      return;
    }

    const el = event.target as HTMLImageElement;
    el.style.display = 'none';
  }
}
